import { useState } from 'react'
import { Landmark } from 'lucide-react'
import { Combobox } from '@/components/Combobox'
import { FormField } from '@/components/FormField'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import { useCreateBankAccount, useRecentBanks } from '@/features/bankAccounts/queries'
import { FAMOUS_US_BANKS } from '@/features/bankAccounts/famousBanks'
import type { BankAccount } from '@/features/bankAccounts/types'

// Generic bank glyph rendered on every bank option in the Combobox. Using
// lucide-react's Landmark keeps us trademark-clean (real bank logos can't
// be shipped without licensing) and visually consistent with the rest of
// the icon set. Tinted with the tenant's brand primary so it picks up
// theme overrides.
const BANK_ICON = <Landmark className="size-4 text-primary" />

interface Props {
  onCreated: (account: BankAccount) => void
  onCancel: () => void
}

/**
 * Inline form embedded inside the BankAccountPicker's "Add new account"
 * modal. Captures bank name + last-4 mask. On 409 BANK_MASK_COLLISION
 * (same tenant + bank + last-4 already exists), re-prompts with last-5.
 *
 * US tenants get a Combobox sourced from FAMOUS_US_BANKS unioned with
 * the tenant's previously-typed banks; non-US tenants get a free-text
 * input.
 */
export function BankAccountInlineForm({ onCreated, onCancel }: Props) {
  const { country } = useRegionalDefaults()
  const recentQ = useRecentBanks()
  const createM = useCreateBankAccount()
  const { toast } = useToast()
  const [bankName, setBankName] = useState('')
  const [mask, setMask] = useState('')
  const [showLast5, setShowLast5] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const recentBanks = recentQ.data?.banks ?? []
  const bankOptions = country === 'US'
    ? Array.from(new Set([...FAMOUS_US_BANKS, ...recentBanks])).map((b) => ({
        value: b,
        label: b,
        icon: BANK_ICON,
      }))
    : []

  const submit = async () => {
    setError(null)
    const cleanBank = bankName.trim()
    const cleanMask = mask.trim()
    if (!cleanMask) {
      setError(showLast5 ? 'Enter the last 5 digits.' : 'Enter the last 4 digits.')
      return
    }
    if (showLast5 ? !/^\d{5}$/.test(cleanMask) : !/^\d{4}$/.test(cleanMask)) {
      setError('Mask must be digits only.')
      return
    }
    try {
      // force_new is always true from this form because the user explicitly
      // clicked "Add new account" - so we want the backend to refuse a
      // silent attach to an existing row and instead surface a 409 with
      // BANK_MASK_COLLISION, which we then handle by re-prompting for
      // last-5 digits below.
      const r = await createM.mutateAsync({
        bank_name: cleanBank || null,
        mask: cleanMask,
        force_new: true,
      })
      toast({ title: 'Bank account added.' })
      onCreated(r.bank_account)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409
          && err.body && typeof err.body === 'object'
          && 'code' in err.body && (err.body as { code: string }).code === 'BANK_MASK_COLLISION') {
        setShowLast5(true)
        setMask('')
        setError('An account at this bank already ends in those 4 digits. Enter the last 5 digits to distinguish them.')
        return
      }
      const msg = err instanceof Error ? err.message : 'Failed to create account.'
      setError(msg)
    }
  }

  return (
    <div className="space-y-3">
      <FormField
        label="Bank name"
        required={country === 'US'}
        control={
          country === 'US' ? (
            <Combobox
              value={bankName || null}
              onChange={(v) => setBankName((v as string) || '')}
              options={bankOptions}
              placeholder="Pick or type a bank"
            />
          ) : (
            <Input
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Your bank's name"
            />
          )
        }
      />
      <FormField
        label={showLast5 ? 'Last 5 digits' : 'Last 4 digits of account'}
        required
        hint={showLast5 ? 'Used to disambiguate two accounts at the same bank with the same last 4.' : undefined}
        control={
          <Input
            value={mask}
            onChange={(e) => setMask(e.target.value.replace(/\D/g, '').slice(0, showLast5 ? 5 : 4))}
            inputMode="numeric"
            pattern={showLast5 ? '\\d{5}' : '\\d{4}'}
            placeholder={showLast5 ? '12345' : '1234'}
          />
        }
      />
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div className="flex items-center justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={createM.isPending}>
          {createM.isPending ? 'Saving…' : 'Add account'}
        </Button>
      </div>
    </div>
  )
}
